import * as z from 'zod'

const sortOptionsSchema = z.string()
export type SortOptions = z.infer<typeof sortOptionsSchema>

const searchBoundsSchema = z.object({
  northEast: z.object({
    lat: z.number(),
    lng: z.number(),
  }),
  southWest: z.object({
    lat: z.number(),
    lng: z.number(),
  }),
})

const searchPartySchema = z.object({
  adults: z.number(),
  childAges: z.number().array(),
})
export type SearchParty = z.infer<typeof searchPartySchema>

const areaSchema = z.object({
  lat: z.number(),
  lng: z.number(),
  isEmptyPoint: z.boolean(),
  rangeMiles: z.number(),
})

const searchFiltersSchema = z.object({
  bookable: z.boolean().optional(),
  categoryIds: z.string().array().optional().default([]),
  page: z.number().optional(),
  limit: z.number().optional(),
  sort: sortOptionsSchema.optional(),
  facets: z.string().array().optional().default([]),
  type: z.enum(['search', 'priceSearch']).optional(),
  hierarchyPath: z.string().optional(),
  area: areaSchema.optional(),
  bounds: searchBoundsSchema.optional(),
  dates: z
    .object({
      arrive: z.string(),
      depart: z.string(),
    })
    .optional(),
  party: searchPartySchema.optional(),
  priceRange: z
    .object({
      min: z.number().default(0),
      max: z.number().default(0),
    })
    .optional(),
  engine: z.number().optional(),
  base: z.string().optional(),
})

export type SearchFilters = z.infer<typeof searchFiltersSchema>

const _photoTransformsSchema = z.object({
  galleryLarge: z.string(),
  gallerySmall: z.string(),
  galleryMedium: z.string(),
})

export type PhotoTransforms = z.infer<typeof _photoTransformsSchema>

const photoSchema = z.object({
  caption: z.string(),
  url: z.object({
    large: z.string(),
    medium: z.string(),
    small: z.string(),
  }),
})

export type Photo = z.infer<typeof photoSchema>

const moneySchema = z.object({
  amount: z.number(),
  amountMinor: z.number().int(),
  currency: z.string(),
})

const categorySchema = z.object({
  id: z.string(),
  rootSlug: z.string(),
  slug: z.string(),
  name: z.string(),
  order: z.number(),
  isByo: z.boolean(),
})

export const campsiteSearchResultSchema = z.object({
  bookable: z.boolean(),
  categories: categorySchema.array().optional(),
  hierarchy: z.string(),
  id: z.string(),
  name: z.string(),
  path: z.string(),
  photo: photoSchema.nullable(),
  point: z.string(),
  lat: z.number(),
  lng: z.number(),
  price: moneySchema.optional(),
  rateCount: z.number(),
  rating10: z.number(),
  rating: z.number(),
  slug: z.string(),
  status: z.enum(['bookable', 'free']),
  leadPriceNights: z.number().nullable().default(1),
  leadPriceAdults: z.number().nullable().default(2),
})

export type CampsiteMapsSearchResult = z.infer<
  typeof campsiteSearchResultSchema
>

export const campsiteMapPointSchema = z.object({
  id: z.string(),
  point: z.string(),
  price: moneySchema.optional(),
  slug: z.string(),
  lat: z.number().optional(),
  lng: z.number().optional(),
})

export const campsiteMapPopupSchema = z.object({
  bookable: z.boolean(),
  categories: z
    .object({
      id: z.string(),
      name: z.string(),
      slug: z.string(),
    })
    .array()
    .optional(),
  hierarchy: z.string(),
  id: z.number(),
  name: z.string(),
  path: z.string(),
  photo: photoSchema.nullable(),
  point: z.string(),
  price: moneySchema.optional(),
  rateCount: z.number(),
  rating10: z.number(),
  slug: z.string(),
  status: z.enum(['bookable', 'free']),
  leadPriceNights: z.number().nullable().default(1),
  leadPriceAdults: z.number().nullable().default(2),
})

export type CampsiteMapPopupSchema = z.infer<typeof campsiteMapPopupSchema>

const latLng = z.object({
  lat: z.number(),
  lng: z.number(),
})

const geoBoundsSchema = z.object({
  northEast: latLng,
  southWest: latLng,
})

const _hierarchyTrailSchema = z.object({
  name: z.string(),
  path: z.string(),
})
export type HierarchyTrail = z.infer<typeof _hierarchyTrailSchema>

const pointSchema = z.object({
  lat: z.number(),
  lng: z.number(),
})

const withinSchema = z.object({
  mi: z.number(),
  km: z.number(),
})

export const mapSearchCampsitesResultsSchemaNonPrice = z.object({
  campsites: campsiteMapPointSchema.array(),
  pagination: z.object({
    currentPage: z.number(),
    totalPages: z.number(),
    totalCount: z.number(),
  }),
  maxPrice: z.number().optional(),
  title: z.string().optional(),
  heading: z.string().optional(),
  sortOptions: sortOptionsSchema.array(),
  sortOption: sortOptionsSchema,
  path: z.string().optional(),
  bounds: geoBoundsSchema.optional().nullable(),
  bookable: z.boolean().optional(),
  bookableCount: z.number().optional(),
  point: pointSchema.optional(),
  category: z.any(),
  count: z.number(),
  leadPrice: z.any(),
  within: withinSchema.optional(),
  cheapestLeadPrice: z
    .tuple([
      moneySchema,
      z.number().nullable().default(1),
      z.number().nullable().default(2),
    ])
    .optional(),
})

export const mapSearchCampsitesResultsSchema = z.object({
  campsites: campsiteSearchResultSchema.array(),
  pagination: z.object({
    currentPage: z.number(),
    totalPages: z.number(),
    totalCount: z.number(),
  }),
  maxPrice: z.number().optional(),
  title: z.string().optional(),
  heading: z.string().optional(),
  sortOptions: sortOptionsSchema.array(),
  sortOption: sortOptionsSchema,
  path: z.string().optional(),
  bounds: geoBoundsSchema.optional().nullable(),
  bookable: z.boolean().optional(),
  bookableCount: z.number().optional(),
  point: pointSchema.optional(),
  category: z.any(),
  count: z.number(),
  leadPrice: z.any(),
  within: withinSchema.optional(),
  cheapestLeadPrice: z
    .tuple([
      moneySchema,
      z.number().nullable().default(1),
      z.number().nullable().default(2),
    ])
    .optional(),
})

export type MapSearchCampsitesResults = z.infer<
  typeof mapSearchCampsitesResultsSchema
>

export const mapSearchCampsiteEndpoint = z
  .function()
  .args(searchFiltersSchema)
  .returns(z.promise(mapSearchCampsitesResultsSchema))

export const mapSearchCampsiteEndpointNonPrice = z
  .function()
  .args(searchFiltersSchema)
  .returns(z.promise(mapSearchCampsitesResultsSchemaNonPrice.optional()))

export type GeoBounds = z.infer<typeof geoBoundsSchema>
// base context
export const mapSearchContextSchema = z.object({
  errorMessage: z.string().optional(),
  errorOccurred: z.boolean().optional(),
  results: mapSearchCampsitesResultsSchema.optional(),
  filters: searchFiltersSchema,
  startTime: z.number().optional(),
  endTime: z.number().optional(),
  page: z.number().default(1),
  bounds: geoBoundsSchema.optional(),
  campsites: z.array(campsiteSearchResultSchema).optional(),
  keyedResults: z.record(z.string(), z.array(z.any())).optional(),
  filtersKey: z.string().optional(),
  languageCode: z.string().optional(),
  logger: z.any().optional(),
  within: withinSchema.optional(),
})
export type MapSearchContext = z.infer<typeof mapSearchContextSchema>
